import moment from 'moment';
import { groupBy, uniqBy } from 'lodash-es';
import type { Types } from '../../types';
import type { ConnectedTypes } from '../../make-connected';
import { idOf } from './util/util';
import { getDayIndex } from '../../../core/util';
import type { DateType } from '../../../common/types';
import { makeChainable } from '../../../common/make-chainable';
import type { Interval } from '../../../core/interfaces';

/**
 * Parses a date input into a moment object.
 *
 * Accepts an ISO date string, a string in 'HH:mm' format, or a DateType.
 * - If the input is in 'HH:mm' format, it is parsed as a UTC time on the epoch date.
 * - Otherwise, the input is parsed as a full UTC date.
 *
 * @param date - The date input to parse.
 * @returns A moment object representing the parsed date and time.
 */
export function parseIntervalDate (date: string | DateType): moment.Moment {
  if (typeof date == 'string') {
    // in case of HH:mm format, parse as UTC time on epoch date
    if (/^\d{1,2}:\d{2}$/.test(date)) {
      return moment.utc(date, 'HH:mm');
    }

    // otherwise parse as full date
    return moment.utc(date);
  }

  return moment.utc(date);
}

/**
 * Removes the shorthand notation (a single day to represent all days)
 */
function standardizeIntervals (
  intervals:     Interval[] | undefined,
  settings:      ConnectedTypes.divisionSettings
): Interval[] | undefined {
  if (intervals?.length == 1) {
    const i = intervals[0];
    const start = parseIntervalDate(i.start);
    const end   = parseIntervalDate(i.end  );
    return Array.from({ length: settings.numDays }, () => ({ start: start.clone(), end: end.clone() }));
  }

  return intervals;
}

/**
 * Converts the intervals to float type intervals
 */
function toFloatIntervals (
  intervals: Interval[] | undefined
): { start: number, end: number }[] | undefined {
  return intervals?.map((i, day: number) => {
    if (!i.start || !i.end) throw new Error(`(RS::To::toFloatIntervals) Interval missing start or end for day ${day}: ${JSON.stringify(i)}`);
    const start = parseFloat(parseIntervalDate(i.start).format('HH.mm'));
    const end   = parseFloat(parseIntervalDate(i.end  ).format('HH.mm'));
    return { start, end };
  });
}

/**
 * Converts
 */
function combineAndMapIntervals (
  _intervals:    Interval[]                  | undefined,
  rootInterval:  ConnectedTypes.rootInterval | undefined | null,
  settings:      ConnectedTypes.divisionSettings
): Types.Shared.Interval[][] | undefined {
  const intervals = toFloatIntervals(standardizeIntervals(_intervals, settings));

  if (rootInterval) {
    if (!intervals) throw new Error('(RS::To::combineAndMapIntervals) no accompanying intervals for rootInterval');
    return makeChainable(rootInterval.intervals)
      .chain(
        x => groupBy(x, i => getDayIndex(i.start)),
        x => Object.entries(x)
          .map(([day, is]) => {
            const limits = intervals.at(parseInt(day));
            if (!limits) throw new Error(`(RS::To::combineAndMapIntervals) Day ${day} not found in dayStartAndEnds`);

            // remove all block intervals that lay outside the day start and end
            return is
              .map(i => ({
                start:  parseFloat(parseIntervalDate(i.start).format('HH.mm')),
                end:    parseFloat(parseIntervalDate(i.end  ).format('HH.mm')),
                binary: true
              } satisfies Types.Shared.Interval))
              .filter(i => i.start >= limits.start && i.end <= limits.end);
          })
          // ensure that all days are represented, even if empty
          .reduce<Types.Shared.Interval[][]>((acc, curr, index) => {
            acc[index] = curr;
            return acc;
          }, Array.from({ length: settings.numDays }, () => []))
      )
      .value;
  }
  if (intervals) {
    return intervals.map(i => [i]);
  }
  return;
};

/**
 * extracts the default interval given the division settings
 */
export function getDefaultInterval (settings: ConnectedTypes.divisionSettings): Interval[] {
  const { dayStart, dayEnd } = settings;
  if (!dayStart) throw new Error('(RS::To::getDefaultInterval) "settings.dayStart" is falsy');
  if (!dayEnd  ) throw new Error('(RS::To::getDefaultInterval) "settings.dayEnd" is falsy');
  return [{ start: dayStart, end: dayEnd }];
}

/**
 * Computes the default interval id given the division settings
 */
export function computesDefaultIntervalId (
  settings: ConnectedTypes.divisionSettings,
  options:  Types.parsedToOptions
): string {
  return idOf.intervalPairReference(getDefaultInterval(settings), undefined, options);
}

/**
 * Extracts all unique intervals and interval-root interval pairs used by the provided entities
 */
export function extractUniqueIntervals (
  settings:    ConnectedTypes.divisionSettings,
  groups:      ConnectedTypes.group[],
  teachers:    ConnectedTypes.teacher[],
  courses:     ConnectedTypes.course[],
  events:      ConnectedTypes.event[],
  lockedTimes: ConnectedTypes.lockedTime[],
  options:  Types.parsedToOptions
): Types.Entities.Interval[] {
  const defaultInterval = getDefaultInterval(settings);

  type InheritedWithId = { id: string, data: [Interval<DateType>[] | undefined, ConnectedTypes.rootInterval | undefined | null] };

  const withRootIntervals = [...groups, ...teachers]
    .map((item): InheritedWithId => {
      const intervals    = item.intervals    ?? defaultInterval;
      const rootInterval = item.rootInterval ?? settings.defaultRootInterval;

      return {
        id:   idOf.intervalPairReference(intervals, rootInterval, options),
        data: [intervals, rootInterval]
      };
    });

  const withoutRootIntervals = [{ intervals: defaultInterval }, ...courses, ...events, ...lockedTimes]
    .map((item): InheritedWithId => {
      const interval = item.intervals ?? ('course' in item ? item.course?.intervals : undefined) ?? defaultInterval;

      return {
        id:   idOf.intervalPairReference(interval, undefined, options),
        data: [interval, undefined]
      };
    });

  return makeChainable(withRootIntervals)
    .chain(
      x => x.concat(withoutRootIntervals),
      x => uniqBy(x, x => x.id),
      x => x
        .map(({ id, data: [_intervals, rootInterval] }) => {
          // compute the corresponding root interval for each interval pair
          const value = combineAndMapIntervals(_intervals, rootInterval, settings);
          if (!value) return;
          return { id, value } as Required<Types.Entities.Interval>;
        })
        .filter(x => x != null)
    )
    .value;
};
